import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { useInvoices, calcTotals } from '../context/InvoicesContext';
import { useDeals } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { fmtMoney, fmtDate, fmtDateFull } from '../lib/format';
import { ArrowLeft, Plus, Trash2, Printer, Send, CheckCircle2, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import { NumberInput } from '../components/ui/number-input';
import BrandSelect from '../components/BrandSelect';
import InvoicePreview from '../components/invoices/InvoicePreview';
import { api } from '../lib/api';
import { toast } from 'sonner';

const mkDate = (offset = 0) => {
  const d = new Date(); d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
};

const emptyInvoice = () => ({
  status: 'draft', brand_id: '', talent_id: '', deal_id: '',
  issue_date: mkDate(0), payment_terms_days: 30, due_date: mkDate(30), paid_date: null,
  line_items: [{ id: 'l' + Date.now(), description: '', quantity: 1, unit_price: 0 }],
  tax_rate: 20, currency: 'GBP', notes: '',
});

const addDays = (iso, n) => {
  const base = iso || mkDate(0);
  const d = new Date(base + 'T00:00:00');
  d.setDate(d.getDate() + (Number(n) || 0));
  return d.toISOString().slice(0, 10);
};

const InvoiceEditor = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const { invoices, addInvoice, updateInvoice, removeInvoice, sender, setSender, talent, statuses, nextNumber } = useInvoices();
  const { deals, brands, updateBrand } = useDeals();
  const { user } = useAuth();
  const isManager = ['manager', 'talent_manager'].includes(user?.role);

  const existing = useMemo(() => invoices.find((i) => i.id === id), [invoices, id]);
  const isNew = !existing;

  const [form, setForm] = useState(existing || emptyInvoice());
  const [previewMode, setPreviewMode] = useState(!isNew && existing?.status !== 'draft');
  const [showSender, setShowSender] = useState(false);
  const [senderForm, setSenderForm] = useState(sender);
  const [addingAltAccount, setAddingAltAccount] = useState(false);
  const [altDraft, setAltDraft] = useState({ label: '', bankName: '', bankAddress: '', accountName: '', accountNumber: '', sortCode: '', swift: '', iban: '' });

  useEffect(() => { if (existing) setForm(existing); }, [existing]);
  useEffect(() => { setSenderForm(sender); }, [sender]);
  // Auto-populate the invoice number on a fresh invoice; user can override.
  useEffect(() => {
    if (isNew && !form.number) {
      (async () => {
        const n = await nextNumber();
        setForm((p) => ({ ...p, number: n }));
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  // Raised date + terms (days) auto-populate the due date; both stay editable.
  const setIssueDate = (v) => setForm((p) => ({ ...p, issue_date: v, due_date: addDays(v, p.payment_terms_days ?? 30) }));
  const setTerms = (v) => setForm((p) => ({ ...p, payment_terms_days: v, due_date: addDays(p.issue_date, v) }));
  const [dealSearch, setDealSearch] = useState('');
  const setLine = (idx, patch) => setForm((p) => ({ ...p, line_items: p.line_items.map((li, i) => (i === idx ? { ...li, ...patch } : li)) }));
  const addLine = () => setForm((p) => ({ ...p, line_items: [...p.line_items, { id: 'l' + Date.now(), description: '', quantity: 1, unit_price: 0 }] }));
  const removeLine = (idx) => setForm((p) => ({ ...p, line_items: p.line_items.filter((_, i) => i !== idx) }));

  const totals = calcTotals(form);

  const loadFromDeal = (dealId) => {
    const d = deals.find((x) => x.id === dealId);
    if (!d) return;
    setForm((p) => ({
      ...p, deal_id: dealId, brand_id: d.brand_id, talent_id: d.talent_id, currency: d.currency,
      line_items: [{ id: 'l' + Date.now(), description: `${d.title} – ${d.type}`, quantity: 1, unit_price: d.value }],
    }));
  };

  // Prefill from a "ready to invoice" suggestion link (e.g. /invoicing/new?deal_id=xyz)
  useEffect(() => {
    const dealId = searchParams.get('deal_id');
    if (isNew && dealId && deals.length) loadFromDeal(dealId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, deals.length]);

  const sanitize = (s) => (s || '').replace(/[^a-zA-Z0-9_-]+/g, '_').replace(/^_+|_+$/g, '') || 'Invoice';
  const monthOf = (iso) => {
    try { return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }).replace(' ', ''); }
    catch { return ''; }
  };
  const pdfFilename = (inv) => {
    const deal = deals.find((d) => d.id === inv.deal_id);
    const jobName = deal?.title || (inv.line_items?.[0]?.description) || inv.number || 'Invoice';
    const month = monthOf(inv.issue_date);
    return `${sanitize(jobName)}_${month}_Invoice`;
  };

  const triggerPdfExport = (inv) => {
    const filename = pdfFilename(inv);
    const originalTitle = document.title;
    document.title = filename;   // browsers use document.title as the default "Save as PDF" filename
    setPreviewMode(true);
    // Wait for React to paint preview mode before opening print dialog
    setTimeout(() => {
      window.print();
      // Restore title after the dialog closes
      setTimeout(() => { document.title = originalTitle; }, 500);
    }, 250);
  };

  const handleSave = async (nextStatus) => {
    if (!isManager) return;
    const payload = { ...form };
    if (nextStatus) payload.status = nextStatus;
    if (nextStatus === 'paid') payload.paid_date = mkDate(0);
    try {
      let saved;
      if (isNew) {
        const newId = await addInvoice({ ...payload, number: payload.number || (await nextNumber()) });
        saved = { ...payload, id: newId };
        nav(`/invoicing/${newId}`);
      } else {
        await updateInvoice(existing.id, payload);
        setForm((p) => ({ ...p, ...payload }));
        saved = { ...existing, ...payload };
      }
      if (nextStatus === 'sent' || nextStatus === 'paid') setPreviewMode(true);
      // Auto-export PDF on every plain save (no status change). Skip auto-export on mark-sent/mark-paid to avoid double-dialogs.
      if (!nextStatus) triggerPdfExport(saved);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async () => {
    if (!existing) return;
    if (window.confirm('Delete this invoice?')) { await removeInvoice(existing.id); nav('/invoicing'); }
  };

  const saveSender = async () => { await setSender(senderForm); setShowSender(false); };

  const additionalAccounts = sender?.additional_accounts || [];
  const saveAltAccount = async () => {
    if (!altDraft.label.trim()) { toast.error('give this account a label, e.g. "USD account"'); return; }
    const account = { id: 'acc_' + Math.random().toString(36).slice(2, 10), ...altDraft };
    try {
      await setSender({ ...sender, additional_accounts: [...additionalAccounts, account] });
      set('bank_account_id', account.id);
      setAddingAltAccount(false);
      setAltDraft({ label: '', bankName: '', bankAddress: '', accountName: '', accountNumber: '', sortCode: '', swift: '', iban: '' });
      toast.success('alternate account saved — it will now appear on this invoice and in the dropdown next time');
    } catch (e) { toast.error(e?.response?.data?.detail || 'failed to save'); }
  };

  return (
    <div className="p-6 lg:p-8" data-emoji-skip="true">
      <div className="flex items-center justify-between mb-6 print:hidden">
        <div className="flex items-center gap-3">
          <Link to="/invoicing" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
            <ArrowLeft className="w-4 h-4" /> invoices
          </Link>
          <span className="text-slate-300">/</span>
          {isNew ? (
            <div className="flex items-center gap-2">
              <label className="text-[11px] uppercase tracking-wide text-slate-400">invoice #</label>
              <Input
                value={form.number || ''}
                onChange={(e) => set('number', e.target.value)}
                placeholder="INV-0307"
                className="h-9 w-40 text-sm font-semibold"
                data-testid="invoice-number-input"
              />
            </div>
          ) : (
            <h1 className="text-lg font-semibold text-slate-800">{form.number}</h1>
          )}
          {!isNew && (
            <span className="ml-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-700 capitalize">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: statuses.find((s) => s.id === form.status)?.color }} />
              {form.status}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden">
            <button onClick={() => setPreviewMode(false)} className={`px-3 h-9 text-sm ${!previewMode ? 'bg-mint-50 text-mint-700' : 'text-slate-600 hover:bg-slate-50'}`}>edit</button>
            <button onClick={() => setPreviewMode(true)} className={`px-3 h-9 text-sm border-l border-slate-200 ${previewMode ? 'bg-mint-50 text-mint-700' : 'text-slate-600 hover:bg-slate-50'}`}>preview</button>
          </div>
          <Button
            variant="ghost"
            className="text-slate-600 hover:bg-slate-100"
            onClick={() => {
              setPreviewMode(true);
              // Give the DOM a tick to switch to preview mode before printing
              setTimeout(() => window.print(), 100);
            }}
            data-testid="print-pdf-btn"
          >
            <Printer className="w-4 h-4 mr-1.5" /> print / PDF
          </Button>
          {isManager && !isNew && form.status !== 'paid' && (
            <Button variant="ghost" className="text-emerald-700 hover:bg-emerald-50" onClick={() => handleSave('paid')}>
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> mark paid
            </Button>
          )}
          {isManager && form.status === 'draft' && (
            <Button variant="ghost" className="text-sky-700 hover:bg-sky-50" onClick={() => handleSave('sent')}>
              <Send className="w-4 h-4 mr-1.5" /> mark sent
            </Button>
          )}
          {isManager && (
            <Button onClick={() => handleSave()} className="bg-mint-500 hover:bg-mint-600 text-slate-900 font-semibold">
              <Save className="w-4 h-4 mr-1.5" /> save
            </Button>
          )}
        </div>
      </div>

      {previewMode || !isManager ? (
        <InvoicePreview invoice={form} sender={sender} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-5">
            <section className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">bill to</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Brand</Label>
                  <BrandSelect value={form.brand_id || ''} onChange={(v) => set('brand_id', v)} placeholder="Select brand" className="mt-1" />
                </div>
                <div>
                  <Label>Talent</Label>
                  <Select value={form.talent_id || ''} onValueChange={(v) => set('talent_id', v)}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select talent" /></SelectTrigger>
                    <SelectContent>{talent.map((t) => <SelectItem key={t.user_id} value={t.user_id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Generate from deal (optional)</Label>
                  <Select value={form.deal_id || ''} onValueChange={loadFromDeal}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Pick a deal to prefill" /></SelectTrigger>
                    <SelectContent>
                      <div className="px-2 py-1.5 sticky top-0 bg-white z-10 border-b border-slate-100">
                        <Input
                          value={dealSearch}
                          onChange={(e) => setDealSearch(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                          placeholder="search deals…"
                          className="h-8 text-xs"
                          data-testid="invoice-deal-search"
                        />
                      </div>
                      {deals.filter((d) => `${d.title || ''}`.toLowerCase().includes(dealSearch.trim().toLowerCase())).map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.title} — {fmtMoney(d.value, d.currency)}</SelectItem>
                      ))}
                      {deals.filter((d) => `${d.title || ''}`.toLowerCase().includes(dealSearch.trim().toLowerCase())).length === 0 && (
                        <div className="px-2 py-3 text-xs text-slate-400 text-center">no deals match</div>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <Label>Raised date</Label>
                    <Input type="date" value={form.issue_date || ''} onChange={(e) => setIssueDate(e.target.value)} className="mt-1" data-testid="invoice-issue-date-input" />
                  </div>
                  <div>
                    <Label>Terms (days)</Label>
                    <Input type="number" min="0" value={form.payment_terms_days ?? 30} onChange={(e) => setTerms(e.target.value === '' ? '' : Number(e.target.value))} className="mt-1" data-testid="invoice-terms-days-input" />
                    <div className="flex items-center gap-1 mt-1.5">
                      {[30, 45, 60].map((d) => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => setTerms(d)}
                          className={`px-2 h-6 rounded-full text-[11px] font-medium border transition-colors ${Number(form.payment_terms_days) === d ? 'bg-pink-500 text-white border-pink-500' : 'bg-white text-slate-600 border-slate-200 hover:border-pink-400'}`}
                          data-testid={`invoice-terms-preset-${d}`}
                        >
                          {d}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label>Due date</Label>
                    <Input type="date" value={form.due_date || ''} onChange={(e) => set('due_date', e.target.value)} className="mt-1" data-testid="invoice-due-date-input" />
                  </div>
                  <div>
                    <Label>PO number</Label>
                    <Input value={form.po_number || ''} onChange={(e) => set('po_number', e.target.value)} placeholder="optional" className="mt-1" data-testid="invoice-po-number-input" />
                  </div>
                </div>
              </div>
            </section>

            <section className="bg-white border border-slate-200 rounded-xl p-5" data-testid="bill-to-section">
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div>
                  <h3 className="text-sm font-semibold text-slate-700">bill to — brand / client invoicing details</h3>
                  <p className="text-xs text-slate-500 mt-0.5">paste the brand's billing block (legal name, address, VAT, PO ref). appears on the printed invoice.</p>
                </div>
                {form.brand_id && (() => {
                  const brand = brands.find((b) => b.id === form.brand_id);
                  const profiles = brand?.billing_profiles || [];
                  const legacy = brand?.billing_details ? [{ id: '__legacy__', label: 'Default', billing_details: brand.billing_details, vat_number: brand.vat_number, invoicing_terms: brand.invoicing_terms }] : [];
                  const allProfiles = profiles.length ? profiles : legacy;
                  return (
                    <div className="flex items-center gap-2">
                      {allProfiles.length > 0 && (
                        <Select onValueChange={(pid) => {
                          if (pid === '__new__') {
                            set('bill_to', '');
                            return;
                          }
                          const bp = allProfiles.find((x) => x.id === pid);
                          if (!bp) return;
                          set('bill_to', bp.billing_details || '');
                          // Honour brand's payment terms for the due date
                          const m = (bp.invoicing_terms || '').match(/(\d+)/);
                          const days = m ? Number(m[1]) : 30;
                          setForm((p) => ({ ...p, payment_terms_days: days, due_date: addDays(p.issue_date, days) }));
                        }}>
                          <SelectTrigger className="h-8 w-48 text-xs bg-white" data-testid="billing-profile-picker">
                            <SelectValue placeholder="use brand profile…" />
                          </SelectTrigger>
                          <SelectContent>
                            {allProfiles.map((bp) => (
                              <SelectItem key={bp.id} value={bp.id}>{bp.label || '(unnamed)'}{bp.invoicing_terms ? ` · ${bp.invoicing_terms}` : ''}</SelectItem>
                            ))}
                            <SelectItem value="__new__" data-testid="new-details-option">+ new details…</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      {allProfiles.length === 0 && form.brand_id && (
                        <Button size="sm" variant="ghost" onClick={() => set('bill_to', '')} className="text-xs text-slate-600 hover:bg-slate-100" data-testid="new-details-btn">
                          + new details
                        </Button>
                      )}
                      {form.bill_to && brand && (
                        <Button size="sm" variant="ghost" onClick={async () => {
                          try {
                            const existing = brand.billing_profiles || [];
                            const newProfile = {
                              id: 'bp_' + Math.random().toString(36).slice(2, 10),
                              label: window.prompt('label for this billing profile (e.g. "Direct", "Via WPP Agency"):') || 'New profile',
                              billing_details: form.bill_to,
                              invoicing_terms: '',
                              vat_number: '',
                            };
                            await updateBrand(brand.id, { billing_profiles: [...existing, newProfile] });
                            toast.success('saved as new profile on the brand');
                          } catch (e) { toast.error(e?.response?.data?.detail || 'failed'); }
                        }} className="text-xs text-pink-600 hover:bg-pink-50" data-testid="save-to-brand-btn">
                          save as new profile ↑
                        </Button>
                      )}
                    </div>
                  );
                })()}
              </div>
              <Textarea
                value={form.bill_to || ''}
                onChange={(e) => set('bill_to', e.target.value)}
                rows={5}
                placeholder={"e.g.\nNike UK Ltd\n1 Victoria Street\nLondon SW1A 1AA\nVAT: GB123456789\nPO: 45678"}
                className="text-sm font-mono"
                data-testid="bill-to-textarea"
              />
            </section>

            <section className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-700">line items</h3>
                <Button variant="ghost" size="sm" onClick={addLine} className="text-mint-700 hover:bg-mint-50"><Plus className="w-4 h-4 mr-1" /> add line</Button>
              </div>
              <div className="space-y-2">
                <div className="hidden md:grid grid-cols-12 gap-2 text-[11px] uppercase tracking-wide text-slate-400 px-1">
                  <div className="col-span-6">description</div>
                  <div className="col-span-2 text-right">qty</div>
                  <div className="col-span-2 text-right">unit</div>
                  <div className="col-span-2 text-right">amount</div>
                </div>
                {(form.line_items || []).map((li, idx) => {
                  const unit = Number(li.unit_price) || 0;
                  const amount = (Number(li.quantity) || 0) * unit;
                  return (
                    <div key={li.id} className="grid grid-cols-12 gap-2 items-center">
                      <Input className="col-span-6" placeholder="Description" value={li.description} onChange={(e) => setLine(idx, { description: e.target.value })} />
                      <NumberInput className="col-span-2 text-right" value={li.quantity} onChange={(v) => setLine(idx, { quantity: v })} placeholder="1" />
                      <NumberInput className="col-span-2 text-right" value={li.unit_price} onChange={(v) => setLine(idx, { unit_price: v })} placeholder="0" />
                      <div className="col-span-1 text-right text-sm font-medium text-slate-800">{fmtMoney(amount, form.currency)}</div>
                      <button onClick={() => removeLine(idx)} className="col-span-1 text-slate-400 hover:text-red-600 justify-self-end p-1">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Notes / payment terms</Label>
                  <Textarea value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} rows={4} className="mt-1" placeholder="Bank details, thank you message, etc." />
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between"><span className="text-slate-500">Subtotal</span><span className="text-slate-800">{fmtMoney(totals.subtotal, form.currency)}</span></div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-slate-500">VAT</span>
                    <div className="flex items-center gap-2">
                      <NumberInput value={form.tax_rate} onChange={(v) => set('tax_rate', v)} placeholder="0" className="w-20 h-8 text-right" />
                      <span className="text-slate-500">%</span>
                      <span className="text-slate-800 w-24 text-right">{fmtMoney(totals.tax, form.currency)}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-slate-500">Currency</span>
                    <Select value={form.currency} onValueChange={(v) => set('currency', v)}>
                      <SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GBP">GBP £</SelectItem>
                        <SelectItem value="USD">USD $</SelectItem>
                        <SelectItem value="EUR">EUR €</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="border-t border-slate-200 pt-2 mt-2 flex items-center justify-between text-base">
                    <span className="font-semibold text-slate-800">Total</span>
                    <span className="font-bold text-slate-900">{fmtMoney(totals.total, form.currency)}</span>
                  </div>
                </div>
              </div>
            </section>

            <section className="bg-white border border-slate-200 rounded-xl p-5" data-testid="alt-bank-section">
              <h3 className="text-sm font-semibold text-slate-700">alternate bank details</h3>
              <p className="text-xs text-slate-500 mt-0.5">use a different account for this invoice only — defaults to the one in your profile settings.</p>
              <div className="mt-3">
                <Select
                  value={addingAltAccount ? '__new__' : (form.bank_account_id || '__default__')}
                  onValueChange={(v) => { if (v === '__new__') { setAddingAltAccount(true); } else { setAddingAltAccount(false); set('bank_account_id', v === '__default__' ? '' : v); } }}
                >
                  <SelectTrigger className="w-full md:w-72" data-testid="alt-bank-select"><SelectValue placeholder="default account" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">default account ({sender?.bankName || 'no bank details saved'})</SelectItem>
                    {additionalAccounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>)}
                    <SelectItem value="__new__" data-testid="alt-bank-add-new">+ add alternate bank details…</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {addingAltAccount && (
                <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
                  <Input placeholder="Label (e.g. USD account)" value={altDraft.label} onChange={(e) => setAltDraft({ ...altDraft, label: e.target.value })} data-testid="alt-bank-label" />
                  <Input placeholder="Bank name" value={altDraft.bankName} onChange={(e) => setAltDraft({ ...altDraft, bankName: e.target.value })} />
                  <Input placeholder="Account name" value={altDraft.accountName} onChange={(e) => setAltDraft({ ...altDraft, accountName: e.target.value })} />
                  <Input placeholder="Bank address" value={altDraft.bankAddress} onChange={(e) => setAltDraft({ ...altDraft, bankAddress: e.target.value })} />
                  <div className="grid grid-cols-2 gap-2">
                    <Input inputMode="numeric" placeholder="Sort code" value={altDraft.sortCode} onChange={(e) => setAltDraft({ ...altDraft, sortCode: e.target.value })} />
                    <Input inputMode="numeric" placeholder="Account #" value={altDraft.accountNumber} onChange={(e) => setAltDraft({ ...altDraft, accountNumber: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="SWIFT / BIC" value={altDraft.swift} onChange={(e) => setAltDraft({ ...altDraft, swift: e.target.value })} />
                    <Input placeholder="IBAN" value={altDraft.iban} onChange={(e) => setAltDraft({ ...altDraft, iban: e.target.value })} />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-mint-500 hover:bg-mint-600 text-slate-900" onClick={saveAltAccount} data-testid="alt-bank-save">save & use on this invoice</Button>
                    <Button size="sm" variant="ghost" onClick={() => setAddingAltAccount(false)}>cancel</Button>
                  </div>
                </div>
              )}
            </section>
          </div>

          <aside className="space-y-5">
            <section className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-700">from</h3>
                <button onClick={() => { setSenderForm(sender); setShowSender((s) => !s); }} className="text-xs text-mint-700 hover:text-mint-800">{showSender ? 'close' : 'edit'}</button>
              </div>
              {!showSender ? (
                <div className="mt-3 text-sm text-slate-700 space-y-1">
                  <div className="font-semibold text-slate-900">{sender?.name}</div>
                  <div className="text-slate-500 whitespace-pre-line text-xs">{sender?.address}</div>
                  <div className="text-xs text-slate-500">{sender?.email}</div>
                  {sender?.vatNumber && <div className="text-xs text-slate-500">VAT: {sender.vatNumber}</div>}
                </div>
              ) : (
                <div className="mt-3 space-y-2">
                  <Input placeholder="Business name" value={senderForm.name || ''} onChange={(e) => setSenderForm({ ...senderForm, name: e.target.value })} />
                  <Input placeholder="Email" value={senderForm.email || ''} onChange={(e) => setSenderForm({ ...senderForm, email: e.target.value })} />
                  <Textarea placeholder="Address" rows={3} value={senderForm.address || ''} onChange={(e) => setSenderForm({ ...senderForm, address: e.target.value })} />
                  <Input placeholder="VAT number" value={senderForm.vatNumber || ''} onChange={(e) => setSenderForm({ ...senderForm, vatNumber: e.target.value })} />
                  <Input placeholder="Bank name" value={senderForm.bankName || ''} onChange={(e) => setSenderForm({ ...senderForm, bankName: e.target.value })} />
                  <Input placeholder="Account name" value={senderForm.accountName || ''} onChange={(e) => setSenderForm({ ...senderForm, accountName: e.target.value })} />
                  <div className="grid grid-cols-2 gap-2">
                    <Input inputMode="numeric" placeholder="Sort code" value={senderForm.sortCode || ''} onChange={(e) => setSenderForm({ ...senderForm, sortCode: e.target.value })} />
                    <Input inputMode="numeric" placeholder="Account #" value={senderForm.accountNumber || ''} onChange={(e) => setSenderForm({ ...senderForm, accountNumber: e.target.value })} />
                  </div>
                  <Input placeholder="IBAN" value={senderForm.iban || ''} onChange={(e) => setSenderForm({ ...senderForm, iban: e.target.value })} />
                  <Button className="w-full bg-mint-500 hover:bg-mint-600 text-slate-900" onClick={saveSender}>save details</Button>
                </div>
              )}
            </section>

            <section className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">status</h3>
              <Select value={form.status} onValueChange={(v) => set('status', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{statuses.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
              <div className="mt-3 text-xs text-slate-500">Issued {fmtDateFull(form.issue_date)} • Due {fmtDateFull(form.due_date)}</div>
            </section>

            {!isNew && (
              <section className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-slate-700 mb-2">danger zone</h3>
                <Button variant="ghost" onClick={handleDelete} className="w-full text-red-600 hover:bg-red-50">
                  <Trash2 className="w-4 h-4 mr-1.5" /> delete invoice
                </Button>
              </section>
            )}
          </aside>
        </div>
      )}
    </div>
  );
};

export default InvoiceEditor;
